import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  ArrowLeft, Copy, Check, Link2, Users, UserCheck, ShoppingBag, Trophy,
  MousePointerClick, Wallet, Store, ExternalLink,
} from 'lucide-react';
import { referralsApi, type ReferralItem } from '../../api/endpoints/referrals';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Badge } from '../../components/ui/Badge';
import { CurrencyDisplay } from '../../components/ui/CurrencyDisplay';
import { useToast } from '../../components/ui/Alert';
import { formatDate, formatNumber, formatCountryName } from '../../lib/formatters';

/**
 * Referral program — account-scoped page.
 *
 * Lives at /referrals, OUTSIDE the /:storeId subtree, because a referral code
 * belongs to the person and not to any one of their stores. Rendered as a
 * standalone screen (like the store picker), not inside DashboardLayout.
 */

const RANGE_OPTIONS = [7, 30, 90];

// One-series daily bar strip. No legend (the heading names the series), one
// hue from the design system's accent token so it stays correct in both
// themes, 2px gaps between bars, rounded data-ends on the baseline.
function VisitsStrip({ series, label }: { series: Array<{ date: string; visits: number; uniqueVisitors: number }>; label: string }) {
  if (!series.length) return null;
  const max = Math.max(...series.map((d) => d.visits), 1);

  return (
    <div>
      <div className="flex h-24 items-end gap-[2px]" role="img" aria-label={label}>
        {series.map((d) => (
          <div
            key={d.date}
            title={`${d.date}: ${d.visits}`}
            className="group relative flex-1 cursor-default rounded-t-[4px] bg-accent/40 transition-colors hover:bg-accent"
            style={{ height: `${Math.max((d.visits / max) * 100, 3)}%` }}
          >
            <span className="pointer-events-none absolute bottom-full start-1/2 z-10 mb-1.5 hidden -translate-x-1/2 whitespace-nowrap rounded-lg border border-border-default bg-bg-secondary px-2 py-1 text-[11px] font-medium text-text-primary shadow-md group-hover:block">
              {d.date} · {formatNumber(d.visits, 'en')}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-[11px] text-text-muted">
        <span>{series[0]?.date}</span>
        <span>{series[series.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function Tile({ icon, label, value, hint }: { icon: React.ReactNode; label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="rounded-xl border border-border-default bg-bg-secondary p-4">
      <div className="flex items-center gap-2 text-text-secondary">
        <span className="text-accent">{icon}</span>
        <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className="mt-2 text-2xl font-bold">{value}</p>
      {hint && <p className="mt-1 text-[11px] text-text-muted">{hint}</p>}
    </div>
  );
}

// Where a referral has reached, as one chip.
function StageBadge({ referral, t }: { referral: ReferralItem; t: TFunction }) {
  if (referral.awards.gmv) return <Badge variant="success">{t('referrals.stageQualified', 'Reached the target')}</Badge>;
  if (referral.firstSaleAt) return <Badge variant="accent">{t('referrals.stageFirstSale', 'Made a first sale')}</Badge>;
  if (referral.verifiedAt) return <Badge variant="info">{t('referrals.stageVerified', 'Verified')}</Badge>;
  return <Badge variant="warning">{t('referrals.stageSignedUp', 'Signed up')}</Badge>;
}

export function ReferralsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [days, setDays] = useState(30);
  const [copied, setCopied] = useState(false);

  const { data: me, isLoading, isError, error, refetch } = useQuery({ queryKey: ['referrals', 'me'], queryFn: referralsApi.me });
  const { data: stats } = useQuery({ queryKey: ['referrals', 'stats', days], queryFn: () => referralsApi.stats(days) });
  const { data: list } = useQuery({ queryKey: ['referrals', 'list'], queryFn: () => referralsApi.list(1, 50) });
  const { data: earnings } = useQuery({ queryKey: ['referrals', 'earnings'], queryFn: referralsApi.earnings });

  const lang = i18n.language === 'en' ? 'en' : 'ar';

  const copyLink = async () => {
    if (!me?.link) return;
    try {
      await navigator.clipboard.writeText(me.link);
      setCopied(true);
      toast('success', t('referrals.copied', 'Link copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast('error', t('referrals.copyFailed', 'Could not copy the link'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-primary">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Never render nothing. A blank screen on a failed load is indistinguishable
  // from a broken route, so say what happened and offer a way out.
  if (isError || !me) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-bg-primary px-6 text-center">
        <p className="text-lg font-semibold">{t('referrals.loadFailed', 'Could not load the referral page')}</p>
        <p className="max-w-md text-sm text-text-muted">
          {(error as Error | null)?.message || t('referrals.loadFailedHint', 'Make sure you are signed in, then try again.')}
        </p>
        <div className="mt-2 flex gap-2">
          <button
            onClick={() => refetch()}
            className="rounded-lg border border-accent/40 bg-accent/15 px-4 py-2 text-sm font-semibold text-accent transition-colors hover:bg-accent/25"
          >
            {t('common.retry', 'Retry')}
          </button>
          <button
            onClick={() => navigate('/')}
            className="rounded-lg border border-border-default px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:text-text-primary"
          >
            {t('referrals.backToStores', 'Back to my stores')}
          </button>
        </div>
      </div>
    );
  }

  const program = me.program;
  const funnel = stats?.funnel || { clicks: 0, signups: 0, verified: 0, firstSales: 0, qualified: 0 };
  const traffic = stats?.traffic;

  return (
    <div className="min-h-screen bg-bg-primary">
      <div className="relative mx-auto max-w-6xl px-6 py-12">
        {/* Header */}
        <button
          onClick={() => navigate('/')}
          className="mb-6 inline-flex items-center gap-2 text-sm text-text-muted transition-colors hover:text-text-primary"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
          {t('referrals.backToStores', 'Back to my stores')}
        </button>

        <div className="mb-8">
          <p className="mb-2 text-sm font-medium tracking-wider text-accent">
            {t('referrals.eyebrow', 'Referral program')}
          </p>
          <h1 className="text-3xl font-bold tracking-tight">{t('referrals.title', 'Invite merchants, earn rewards')}</h1>
          <p className="mt-3 max-w-2xl text-sm text-text-muted">
            {t('referrals.subtitle', 'Share your link. When a merchant you brought in makes their first real sale, and again when they reach the sales target, you earn a reward.')}
          </p>
        </div>

        {me.status === 'suspended' && (
          <div className="mb-6 rounded-xl border border-danger/30 bg-danger/10 px-5 py-3.5 text-sm font-medium text-danger">
            {t('referrals.suspended', 'Your participation in the referral program is currently paused. Please contact support.')}
          </div>
        )}

        {!program.active && (
          <div className="mb-6 rounded-xl border border-warning/30 bg-warning/10 px-5 py-3.5 text-sm font-medium text-warning">
            {t('referrals.programPaused', 'The referral program is paused right now. Existing referrals and balances are unaffected.')}
          </div>
        )}

        {/* Link + terms */}
        <div className="mb-8 grid gap-4 lg:grid-cols-3">
          <div className="rounded-xl border border-accent/25 bg-accent/8 p-5 lg:col-span-2">
            <div className="flex items-center gap-2 text-accent">
              <Link2 className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">{t('referrals.yourLink', 'Your referral link')}</span>
            </div>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
              <code dir="ltr" className="flex-1 truncate rounded-lg border border-border-default bg-bg-primary px-3 py-2.5 text-sm text-text-primary">
                {me.link}
              </code>
              <button
                onClick={copyLink}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-accent/40 bg-accent/15 px-4 py-2.5 text-sm font-semibold text-accent transition-colors hover:bg-accent/25"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? t('referrals.copied', 'Link copied') : t('referrals.copy', 'Copy')}
              </button>
            </div>
            <p className="mt-3 text-xs text-text-muted">
              {t('referrals.cookieNote', 'Anyone who signs up within {{days}} days of opening your link is credited to you.', { days: program.cookieDays })}
            </p>
          </div>

          <div className="rounded-xl border border-border-default bg-bg-secondary p-5">
            <span className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
              {t('referrals.rewardsTitle', 'What you earn')}
            </span>
            <ul className="mt-3 space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <ShoppingBag className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <span>
                  <CurrencyDisplay amount={program.firstSaleReward} currency={program.currency} className="font-bold" />
                  <span className="text-text-muted"> · {t('referrals.rewardFirstSale', 'when they make their first sale')}</span>
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <Trophy className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <span>
                  <CurrencyDisplay amount={program.gmvReward} currency={program.currency} className="font-bold" />
                  <span className="text-text-muted">
                    {' · '}
                    {t('referrals.rewardGmv', 'when their sales reach {{threshold}} within {{days}} days', {
                      threshold: formatNumber(program.gmvThreshold, 'en'),
                      days: program.windowDays,
                    })}
                  </span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Funnel */}
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-secondary">
          {t('referrals.funnelTitle', 'Your funnel')}
        </h2>
        <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Tile icon={<MousePointerClick className="h-4 w-4" />} label={t('referrals.clicks', 'Clicks')} value={formatNumber(funnel.clicks, 'en')} hint={t('referrals.clicksHint', 'Bots excluded')} />
          <Tile icon={<Users className="h-4 w-4" />} label={t('referrals.signups', 'Signups')} value={formatNumber(funnel.signups, 'en')} />
          <Tile icon={<UserCheck className="h-4 w-4" />} label={t('referrals.verified', 'Verified')} value={formatNumber(funnel.verified, 'en')} />
          <Tile icon={<ShoppingBag className="h-4 w-4" />} label={t('referrals.firstSales', 'First sales')} value={formatNumber(funnel.firstSales, 'en')} />
          <Tile icon={<Trophy className="h-4 w-4" />} label={t('referrals.qualified', 'Reached target')} value={formatNumber(funnel.qualified, 'en')} />
        </div>

        {/* Traffic */}
        <div className="mb-8 rounded-xl border border-border-default bg-bg-secondary p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-text-secondary">
              {t('referrals.trafficTitle', 'Link traffic')}
            </h2>
            <div className="flex gap-1 rounded-lg border border-border-default bg-bg-primary p-1">
              {RANGE_OPTIONS.map((option) => (
                <button
                  key={option}
                  onClick={() => setDays(option)}
                  className={
                    days === option
                      ? 'rounded-md bg-accent/20 px-3 py-1 text-xs font-semibold text-accent'
                      : 'rounded-md px-3 py-1 text-xs font-medium text-text-muted transition-colors hover:text-text-primary'
                  }
                >
                  {t('referrals.lastDays', '{{count}} days', { count: option })}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="grid grid-cols-3 gap-3 lg:col-span-1 lg:grid-cols-1">
              <Tile icon={<MousePointerClick className="h-4 w-4" />} label={t('referrals.visits', 'Visits')} value={formatNumber(traffic?.visits || 0, 'en')} />
              <Tile icon={<Users className="h-4 w-4" />} label={t('referrals.uniqueVisitors', 'Unique visitors')} value={formatNumber(traffic?.uniqueVisitors || 0, 'en')} />
              <Tile icon={<Link2 className="h-4 w-4" />} label={t('referrals.sessions', 'Sessions')} value={formatNumber(traffic?.sessions || 0, 'en')} />
            </div>

            <div className="lg:col-span-2">
              {traffic?.series?.length ? (
                <VisitsStrip series={traffic.series} label={t('referrals.dailyVisits', 'Daily visits')} />
              ) : (
                <p className="py-8 text-center text-sm text-text-muted">{t('referrals.noTraffic', 'No visits recorded yet')}</p>
              )}
            </div>
          </div>

          {(traffic?.countries?.length || traffic?.devices?.length) ? (
            <div className="mt-5 grid gap-5 border-t border-border-default pt-5 sm:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">{t('referrals.topCountries', 'Top countries')}</p>
                <ul className="space-y-1.5 text-sm">
                  {(traffic?.countries || []).slice(0, 5).map((c) => (
                    <li key={c.country} className="flex justify-between">
                      <span>{formatCountryName(c.country, lang)}</span>
                      <span className="text-text-muted">{formatNumber(c.uniqueVisitors, 'en')}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">{t('referrals.devices', 'Devices')}</p>
                <ul className="space-y-1.5 text-sm">
                  {(traffic?.devices || []).map((d) => (
                    <li key={d.device} className="flex justify-between">
                      <span>{t(`referrals.device_${d.device}`, d.device)}</span>
                      <span className="text-text-muted">{formatNumber(d.visits, 'en')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}

          <p className="mt-4 text-[11px] text-text-muted">
            {t('referrals.trafficNote', 'Traffic detail covers the last 90 days. Funnel totals above are lifetime.')}
          </p>
        </div>

        {/* Earnings */}
        <div className="mb-8 grid gap-4 lg:grid-cols-3">
          <div className="rounded-xl border border-border-default bg-bg-secondary p-5">
            <div className="flex items-center gap-2 text-text-secondary">
              <Wallet className="h-4 w-4 text-accent" />
              <span className="text-xs font-semibold uppercase tracking-wider">{t('referrals.balance', 'Balance')}</span>
            </div>
            <p className="mt-2 text-3xl font-bold">
              <CurrencyDisplay amount={earnings?.balance ?? me.balance} currency={me.currency} />
            </p>
            <p className="mt-2 text-xs text-text-muted">
              {t('referrals.pendingAmount', 'Awaiting approval')}: <CurrencyDisplay amount={me.totals?.pending || 0} currency={me.currency} />
            </p>
            <p className="mt-1 text-[11px] text-text-muted">
              {t('referrals.minPayout', 'Minimum payout: {{amount}}', { amount: formatNumber(program.minPayout, 'en') })}
            </p>
          </div>

          <div className="rounded-xl border border-border-default bg-bg-secondary p-5 lg:col-span-2">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-secondary">
              {t('referrals.ledgerTitle', 'Balance history')}
            </p>
            {earnings?.ledger?.length ? (
              <ul className="divide-y divide-border-default">
                {earnings.ledger.slice(0, 6).map((entry) => (
                  <li key={entry.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{(lang === 'en' ? entry.descriptionEn : entry.description) || ''}</p>
                      <p className="text-xs text-text-muted">
                        {formatDate(entry.createdAt, 'dd MMM yyyy', lang)}
                        {entry.storeName ? ` · ${entry.storeName}` : ''}
                      </p>
                    </div>
                    <span className={entry.kind === 'credit' ? 'shrink-0 font-bold text-success' : 'shrink-0 font-bold text-danger'}>
                      {entry.kind === 'credit' ? '+' : ''}
                      <CurrencyDisplay amount={entry.amount} currency={entry.currency} />
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="py-6 text-center text-sm text-text-muted">{t('referrals.noEarnings', 'Nothing earned yet')}</p>
            )}
          </div>
        </div>

        {/* Referred merchants */}
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-secondary">
          {t('referrals.merchantsTitle', 'Merchants you referred')}
        </h2>
        <div className="overflow-hidden rounded-xl border border-border-default bg-bg-secondary">
          {list?.referrals?.length ? (
            <div className="divide-y divide-border-default">
              {list.referrals.map((referral) => {
                const progress = Math.min((referral.gmv / (referral.gmvThreshold || 1)) * 100, 100);
                return (
                  <div key={referral.id} className="p-4 sm:p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold">{referral.merchant}</p>
                        <p className="text-xs text-text-muted">
                          {t('referrals.signedUpOn', 'Signed up {{date}}', { date: formatDate(referral.signupAt, 'dd MMM yyyy', lang) })}
                          {' · '}
                          {referral.windowOpen
                            ? t('referrals.windowOpen', 'Window closes {{date}}', { date: formatDate(referral.windowEndsAt, 'dd MMM yyyy', lang) })
                            : t('referrals.windowClosed', 'Window closed')}
                        </p>
                      </div>
                      <StageBadge referral={referral} t={t} />
                    </div>

                    {/* Stores this merchant runs */}
                    {referral.stores.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {referral.stores.map((store) => (
                          <a
                            key={store.slug || store.name}
                            href={store.slug ? `https://${store.slug}.00stores.com` : undefined}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-lg border border-border-default bg-bg-primary px-2.5 py-1 text-xs text-text-secondary transition-colors hover:text-text-primary"
                          >
                            <Store className="h-3 w-3" />
                            {store.name || store.slug}
                            {store.slug && <ExternalLink className="h-3 w-3 opacity-60" />}
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Progress toward the sales target */}
                    <div className="mt-3">
                      <div className="mb-1.5 flex justify-between text-xs">
                        <span className="text-text-muted">{t('referrals.salesProgress', 'Sales toward the target')}</span>
                        <span className="font-medium">
                          <CurrencyDisplay amount={referral.gmv} currency={me.currency} />
                          <span className="text-text-muted"> / {formatNumber(referral.gmvThreshold, 'en')}</span>
                        </span>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded-full bg-bg-tertiary">
                        <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${progress}%` }} />
                      </div>
                    </div>

                    {/* Rewards earned from this merchant */}
                    {(referral.awards.firstSale || referral.awards.gmv) && (
                      <div className="mt-3 flex flex-wrap gap-2 text-xs">
                        {referral.awards.firstSale && (
                          <Badge variant={referral.awards.firstSale.state === 'approved' ? 'success' : 'warning'}>
                            {t('referrals.rewardFirstSaleShort', 'First sale')}: <CurrencyDisplay amount={referral.awards.firstSale.amount} currency={me.currency} />
                          </Badge>
                        )}
                        {referral.awards.gmv && (
                          <Badge variant={referral.awards.gmv.state === 'approved' ? 'success' : 'warning'}>
                            {t('referrals.rewardGmvShort', 'Sales target')}: <CurrencyDisplay amount={referral.awards.gmv.amount} currency={me.currency} />
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <Users className="mx-auto mb-3 h-8 w-8 text-text-muted" />
              <p className="text-sm font-medium">{t('referrals.noReferrals', 'No referrals yet')}</p>
              <p className="mt-1 text-xs text-text-muted">{t('referrals.noReferralsHint', 'Share your link to get started.')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
